import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  EspdResponseDto,
  PortalSourcingEventDetailDto,
  PublicBidDto,
  PublicBidPatchDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { emptyEspd } from "../espd/useEspdAnswers";
import { isPublicBidEditable } from "../publicBidEditability";

interface Props {
  bidId: string;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const SupplierInfoStep = ({
  bidId,
  event,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();

  const supplierDeclarations = event.applicableSupplierDeclarations ?? [];
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { state, updateField, fieldStatus } = useEditor<
    PublicBidDto,
    PublicBidPatchDto
  >(bidId);
  const readOnly = !isPublicBidEditable(state);

  const espd: EspdResponseDto = state.espdResponse ?? emptyEspd();
  const eo = espd.economicOperator ?? {
    smeIndicator: null,
    representatives: [],
  };
  const rep = eo.representatives?.[0] ?? { firstName: "", familyName: "" };

  const saveEspd = (next: EspdResponseDto) => {
    if (readOnly) return;
    updateField({ espdResponse: next }, { debounceMs: 300 });
  };
  const setSme = (yes: boolean) =>
    saveEspd({ ...espd, economicOperator: { ...eo, smeIndicator: yes } });
  const setRep = (patch: Partial<typeof rep>) =>
    saveEspd({
      ...espd,
      economicOperator: { ...eo, representatives: [{ ...rep, ...patch }] },
    });
  const answerFor = (id: string): boolean | null =>
    espd.responses?.find((r) => r.requirementId === id)?.indicator ?? null;
  const setAnswer = (id: string, yes: boolean) => {
    const others = (espd.responses ?? []).filter((r) => r.requirementId !== id);
    saveEspd({
      ...espd,
      responses: [...others, { requirementId: id, indicator: yes }],
    });
  };

  const isSaving =
    fieldStatus("espdResponse") === "saving" ||
    fieldStatus("espdResponse") === "pending";

  return (
    <Box pb="8" pt="6">
      <Flex align="center" gap="2" mb="6">
        <Heading size="md">{t("About the supplier")}</Heading>
        {isSaving && <Spinner size="sm" color="smMuted" />}
      </Flex>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="4">
            {t("Identification")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="8" spacingY="4">
            <Field label={t("Supplier")} value={selectedOrg.name} />
            {selectedOrg.organizationNumber && (
              <Field
                label={t("Organization number")}
                value={selectedOrg.organizationNumber}
              />
            )}
            {selectedOrg.country?.name && (
              <Field label={t("Country")} value={selectedOrg.country.name} />
            )}
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="1">
            {t("Authorized representative")}
          </Heading>
          <Text fontSize="sm" color="smMuted" mb="4">
            {t("The person who can sign the offer on behalf of the supplier.")}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="6" spacingY="4">
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("First name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.firstName}
                onChange={(e) => setRep({ firstName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Family name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.familyName}
                onChange={(e) => setRep({ familyName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Email")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                type="email"
                value={rep.email ?? ""}
                onChange={(e) => setRep({ email: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t("Role")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.role ?? ""}
                onChange={(e) => setRep({ role: e.target.value })}
              />
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card variant="outline" rounded="xl" mb="8">
        <CardBody>
          <Heading size="sm" mb="4">
            {t("Self-declaration")}
          </Heading>
          <Stack spacing="5">
            <Question
              text={t(
                "Is the supplier a micro, small or medium-sized enterprise (SME)?",
              )}
              value={eo.smeIndicator ?? null}
              onChange={setSme}
              yesLabel={t("Yes")}
              noLabel={t("No")}
              readOnly={readOnly}
            />
            {supplierDeclarations.map((criterion) => (
              <Question
                key={criterion.id}
                text={t(criterion.type)}
                value={answerFor(criterion.id)}
                onChange={(yes) => setAnswer(criterion.id, yes)}
                yesLabel={t("Yes")}
                noLabel={t("No")}
                readOnly={readOnly}
              />
            ))}
          </Stack>
        </CardBody>
      </Card>

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
      />
    </Box>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Text
      fontSize="xs"
      color="smMuted"
      textTransform="uppercase"
      letterSpacing="wide"
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {value}
    </Text>
  </Box>
);

const Question = ({
  text,
  value,
  onChange,
  yesLabel,
  noLabel,
  readOnly,
}: {
  text: string;
  value: boolean | null;
  onChange: (yes: boolean) => void;
  yesLabel: string;
  noLabel: string;
  readOnly: boolean;
}) => (
  <Box borderWidth="1px" borderRadius="md" p="4">
    <Text fontSize="sm" fontWeight="medium" mb="3">
      {text}
    </Text>
    <HStack spacing="2">
      <Button
        size="sm"
        variant={value === true ? "solid" : "outline"}
        colorScheme="teal"
        isDisabled={readOnly}
        onClick={() => onChange(true)}
      >
        {yesLabel}
      </Button>
      <Button
        size="sm"
        variant={value === false ? "solid" : "outline"}
        colorScheme="teal"
        isDisabled={readOnly}
        onClick={() => onChange(false)}
      >
        {noLabel}
      </Button>
    </HStack>
  </Box>
);
